import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, MapPin, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";

export const Route = createFileRoute("/(public)/onboarding/locations")({
  component: AddLocationsPage,
});

function RegionAccordion({
  regionName,
  companyName,
  locations,
  expanded,
  inputValue,
  onToggle,
  onInputChange,
  onAdd,
  onRemove,
}: {
  regionName: string;
  companyName: string;
  locations: string[];
  expanded: boolean;
  inputValue: string;
  onToggle: () => void;
  onInputChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (name: string) => void;
}) {
  const { t } = useLingui();
  return (
    <div className="rounded-md overflow-hidden border border-(--gray-200) bg-(--brand-white)">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between h-12 px-4 transition-colors hover:bg-(--gray-50)"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium shrink-0 text-(--brand-black)">
            {regionName}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded truncate bg-(--gray-100) text-(--gray-600)">
            {companyName}
          </span>
          {locations.length > 0 && (
            <span className="text-xs shrink-0 text-(--gray-500)">({locations.length})</span>
          )}
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 shrink-0 text-(--gray-500)" />
          : <ChevronRight className="w-4 h-4 shrink-0 text-(--gray-500)" />
        }
      </button>

      {expanded && (
        <div className="flex flex-col gap-2.5 px-4 pb-4 pt-2" style={{ borderTop: "1px solid var(--gray-100)" }}>
          {locations.map((loc) => (
            <div
              key={loc}
              className="flex items-center justify-between h-10 px-3 rounded-md border border-(--gray-200) bg-(--gray-50)"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-(--gray-500)" />
                <span className="text-sm text-(--brand-black)">{loc}</span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(loc)}
                className="w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-red-50 text-(--color-danger)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <input
            type="text"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
            placeholder={t`Enter location name...`}
            className="w-full h-10 rounded-md px-3 text-sm outline-none transition-shadow border border-(--gray-200) text-(--brand-black) bg-(--brand-white)"
            onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
            onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
          />

          <button
            type="button"
            onClick={onAdd}
            className="flex items-center gap-1.5 text-sm font-medium w-fit transition-opacity hover:opacity-70 text-(--gray-600)"
          >
            <Plus className="w-4 h-4" />
            {t`Add another location`}
          </button>
        </div>
      )}
    </div>
  );
}

function AddLocationsPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { regionsByCompany, locationsByRegion: savedLocs, setLocationsByRegion } = useOnboarding();

  const allRegions = Object.values(regionsByCompany).flat();

  const regionToCompany = Object.fromEntries(
    Object.entries(regionsByCompany).flatMap(([company, regions]) =>
      regions.map((r) => [r, company]),
    ),
  );

  const [locationsByRegion, setLocal] = useState<Record<string, string[]>>(
    () => Object.fromEntries(allRegions.map((r) => [r, savedLocs[r] ?? []])),
  );
  const [expandedByRegion, setExpanded] = useState<Record<string, boolean>>(
    () => Object.fromEntries(allRegions.map((r) => [r, true])),
  );
  const [inputByRegion, setInput] = useState<Record<string, string>>({});

  const addLocation = (regionName: string) => {
    const name = (inputByRegion[regionName] ?? "").trim();
    if (!name) return;
    const existing = locationsByRegion[regionName] ?? [];
    if (existing.some((l) => l.toLowerCase() === name.toLowerCase())) {
      toast.error(t`Location already added`);
      return;
    }
    setLocal((prev) => ({ ...prev, [regionName]: [...existing, name] }));
    setInput((prev) => ({ ...prev, [regionName]: "" }));
  };

  const removeLocation = (regionName: string, loc: string) => {
    setLocal((prev) => ({
      ...prev,
      [regionName]: (prev[regionName] ?? []).filter((l) => l !== loc),
    }));
  };

  const handleContinue = () => {
    setLocationsByRegion(locationsByRegion);
    navigate({ to: "/onboarding/invite" });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Add your first location`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Locations represent individual kitchens or restaurants where recipes are used.`}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {allRegions.length === 0 ? (
          <p className="text-sm text-(--gray-400)">
            {t`No regions added yet. You can skip this step and add locations later.`}
          </p>
        ) : (
          allRegions.map((regionName) => (
            <RegionAccordion
              key={regionName}
              regionName={regionName}
              companyName={regionToCompany[regionName] ?? ""}
              locations={locationsByRegion[regionName] ?? []}
              expanded={expandedByRegion[regionName] ?? true}
              inputValue={inputByRegion[regionName] ?? ""}
              onToggle={() => setExpanded((prev) => ({ ...prev, [regionName]: !prev[regionName] }))}
              onInputChange={(v) => setInput((prev) => ({ ...prev, [regionName]: v }))}
              onAdd={() => addLocation(regionName)}
              onRemove={(loc) => removeLocation(regionName, loc)}
            />
          ))
        )}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleContinue}
          className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 bg-(--brand-black)"
        >
          {t`Continue`} <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => navigate({ to: "/onboarding/invite" })}
          className="text-sm font-medium text-center transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          {t`Skip for now`}
        </button>
      </div>
    </div>
  );
}
